import {Component, OnInit, signal, computed} from '@angular/core';
import {CommonModule} from '@angular/common';
import {InfoPageHeaderComponent} from '../../shared/components/info-page-header/info-page-header.component';
import {FormBuilder, Validators, ReactiveFormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {SetCheckInAdultBuilder} from '../../shared/models/set-check-in-adult.model';
import {CheckInService} from '../../core/api/check-in.service';
import {HoomvipStorage} from '../../core/hoomvip-storage/hoomvip-storage.service';
import {MrzFields, MrzFieldsService} from '../../core/services/mrz-fields.service';
import {ContractTypeService} from '../../core/api/contract-type.service';
import {ContractType} from '../../shared/models/contract-type.model';
import {Country} from '../../shared/models/country.model';
import {CountryService} from '../../core/api/country.service';
import {ModalInfoService} from '../../core/services/modal-info.service';
import {CheckInValidators} from '../../core/services/check-in.validators';
import {CheckInBaseComponent} from '../../shared/components/check-in-base/check-in-base.component';
import {ProvinceService} from '../../core/api/province.service';
import {MunicipalityService} from '../../core/api/municipality.service';
import {ModalErrorService} from '../../core/services/modal-error.service';
import {MetricsService} from '../../core/services/metrics.service';
import {ReservationService} from '../../core/api/reservation.service';
import {requiresContract} from '../../shared/models/reservation.model';
import {SetCheckInMinorBuilder} from '../../shared/models/set-check-in-minor.model';

export const CHECK_IN_TYPE = {
  ADULT: 'Adulto',
  MINOR: 'Menor de edad'
};

@Component({
  selector: 'app-check-in-form',
  templateUrl: './check-in-form.component.html',
  styleUrls: ['./check-in-form.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, InfoPageHeaderComponent],
})
export class CheckInFormComponent extends CheckInBaseComponent implements OnInit {
  birthdate = signal<string>('');
  age = computed(() => {
    const val = this.birthdate();
    if (val) return this.calculateAge(val);
    if (this.hoomvipStorage.getCheckInType() == CHECK_IN_TYPE.ADULT) {
      return 18;
    }
    return 0;
  });

  sublabel = computed(() => {
    const ageVal = this.age();
    const val = this.birthdate();
    if (!val) return this.hoomvipStorage.getCheckInType() || '';
    if (ageVal < 14) {
      return 'Menor de edad (Menor de 14 años)';
    } else if (ageVal >= 14 && ageVal < 18) {
      return 'Menor de edad (14-17 años)';
    } else {
      return 'Adulto';
    }
  });
  contractTypes: ContractType[] = [];
  countries: Country[] = [];
  test = false;
  mrz: MrzFields | null = null;
  contractNecessary = signal(false);

  constructor(
    fb: FormBuilder,
    private router: Router,
    private checkInService: CheckInService,
    private contractTypeService: ContractTypeService,
    private hoomvipStorage: HoomvipStorage,
    private mrzState: MrzFieldsService,
    private countryService: CountryService,
    modalErrorService: ModalErrorService,
    provinceService: ProvinceService,
    municipalityService: MunicipalityService,
    private modalInfoService: ModalInfoService,
    private metricsService: MetricsService,
    private reservationService: ReservationService,
  ) {
    super(fb, provinceService, municipalityService, modalErrorService);

    this.form = this.fb.group({
      nacionalidad: ['', Validators.required],
      fechaNacimiento: ['', Validators.required],
      nombre: ['', Validators.required],
      apellido1: ['', Validators.required],
      apellido2: [''],
      sexo: ['', Validators.required],
      tipoDocumento: ['D', Validators.required],
      numeroDocumento: ['', [Validators.required, CheckInValidators.documentNumberByType('tipoDocumento')]],
      soporteDocumento: ['', CheckInValidators.requiredWhenDocumentTypeIs('tipoDocumento', ['D', 'N'])],
      fechaExpedicion: ['', Validators.required],
      fechaCaducidad: [''],
      pais: ['', Validators.required],
      provincia: ['', CheckInValidators.requiredWhenCountryIs('pais', ['ESP'])],
      municipio: ['', CheckInValidators.requiredWhenCountryIs('pais', ['ESP'])],
      estado: ['', CheckInValidators.requiredWhenCountryIsNot('pais', ['ESP'])],
      ciudad: ['', CheckInValidators.requiredWhenCountryIsNot('pais', ['ESP'])],
      direccion: ['', Validators.required],
      codigoPostal: ['', Validators.required],
      email: ['', CheckInValidators.strictEmail()],
      tipoContrato: [''],
    });

    this.form.get('fechaNacimiento')?.valueChanges.subscribe((value) => {
      this.birthdate.set(value || '');
      this.updateFormFieldsByAge();
    })

    this.form.get('tipoDocumento')?.valueChanges.subscribe(() => {
      this.form.get('numeroDocumento')?.updateValueAndValidity();
      this.form.get('soporteDocumento')?.updateValueAndValidity();
    });

    this.form.get('nacionalidad')?.valueChanges.subscribe(() => {
      this.onNationalityChanged();
    });

    this.form.get('pais')?.valueChanges.subscribe(() => {
      this.onCountryChanged();
      this.updateAddressFieldsValidity();
    });

    this.form.get('provincia')?.valueChanges.subscribe((province) => {
      this.onProvinceChanged(province);
    });
  }

  ngOnInit(): void {
    this.updateFormFieldsByAge();
    if (this.mrzState.isSuccess()) {
      const mrzData = this.mrzState.consume();
      if (mrzData) {
        this.mrz = mrzData;
        this.applyMrzToForm(mrzData);
      }
    }

    this.reservationService.getReservation().subscribe({
      next: (reservation) => {
        this.contractNecessary.set(requiresContract(reservation));
        console.log('Contract necessary:', this.contractNecessary());
        const tipoContratoControl = this.form.get('tipoContrato');

        if (this.contractNecessary()) {
          tipoContratoControl?.setValidators([Validators.required]);
          this.contractTypeService.getContractTypes().subscribe({
            next: (types) => {
              this.contractTypes = Array.isArray(types) ? types : [];
              if (this.contractTypes.length == 1) {
                tipoContratoControl?.setValue(this.contractTypes[0].id);
                this.contractNecessary.set(false);
              }
            },
            error: (err) => {
              console.error('Error cargando tipos de contrato:', err);
              this.contractTypes = [];
            }
          });
        } else {
          tipoContratoControl?.clearValidators();
          this.contractTypes = [];
          tipoContratoControl?.setValue('');
        }

        tipoContratoControl?.updateValueAndValidity();
      },
      error: (err) => {
        console.error('Error cargando reserva:', err);
      }
    });

    this.countryService.getCountries().subscribe({
      next: (countries) => this.countries = Array.isArray(countries) ? countries : [],
      error: (err) => {
        console.error('Error cargando paises:', err);
        this.countries = [];
      }
    });

    if (this.isSpainSelected()) {
      this.loadProvinces();
    }
  }

  onSubmit(): void {
    console.log('Submitting form');

    if (this.loading()) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.modalErrorService.open('Revisa estos campos', this.getInvalidFieldMessages());
      return;
    }

    const formValue = this.form.getRawValue();
    const isSpain = this.isSpainSelected();
    const ageVal = this.age();

    this.loading.set(true);

    // campos compartidos
    const sexo = formValue.sexo;
    const nombre = this.normalizeString(formValue.nombre);
    const apellido1 = this.normalizeString(formValue.apellido1);
    const apellido2 = this.normalizeString(formValue.apellido2);
    const fechaNacimiento = this.normalizeString(formValue.fechaNacimiento);
    const direccion = this.normalizeString(formValue.direccion);
    const codigoPostal = this.normalizeString(formValue.codigoPostal);
    const pais = this.normalizeUppercaseString(formValue.pais);
    const email = this.normalizeString(formValue.email);
    const nacionalidad = this.normalizeUppercaseString(formValue.nacionalidad);

    const provinceId = isSpain ? this.normalizeString(formValue.provincia) : '';
    const municipalityId = isSpain ? Number.parseInt(formValue.municipio, 10) || 0 : null;
    const cityAddress = isSpain
      ? this.normalizeString(this.municipalities.find(m => m.id === municipalityId)?.name ?? '')
      : this.normalizeString(formValue.ciudad);
    const stateAddress = isSpain ? '' : this.normalizeString(formValue.estado);

    if (ageVal < 14) {
      const checkInMinorBuilder = new SetCheckInMinorBuilder(this.hoomvipStorage.getCode())
        .withPersonalInfo(nombre, apellido1 + ' ' + apellido2, apellido1, apellido2, sexo)
        .withBirthDate(fechaNacimiento)
        .withNationality(nacionalidad, pais)
        .withAddress(direccion, cityAddress, stateAddress, codigoPostal, provinceId, municipalityId);

      this.checkInService.setCheckInMinor(checkInMinorBuilder.build()).subscribe({
        next: (data) => {
          this.metricsService.checkInMinorRegistered();
          this.hoomvipStorage.unmarkCheckingWarning();
          this.loading.set(false);
          this.hoomvipStorage.setCheckInMinorSignatureInfo(data.travelRegisterId, email);
          this.router.navigate(['/check-in-signature-minor']);
        },
        error: (error) => {
          this.loading.set(false);
          this.modalErrorService.open('Error en el check-in',
            this.modalErrorService.extractMessages(error));
        }
      });
    } else {
      const tipoDocumento = this.normalizeUppercaseString(formValue.tipoDocumento);
      const numeroDocumento = this.normalizeUppercaseString(formValue.numeroDocumento);
      const soporteDocumento = this.shouldShowSoporteDocumento(tipoDocumento)
        ? this.normalizeUppercaseString(formValue.soporteDocumento)
        : '';
      const fechaExpedicion = this.normalizeString(formValue.fechaExpedicion);
      const fechaCaducidad = this.normalizeString(formValue.fechaCaducidad);
      const tipoContrato = formValue.tipoContrato;
      const checkInData = new SetCheckInAdultBuilder(this.hoomvipStorage.getCode())
        .withPersonalInfo(sexo, nombre, apellido1 + ' ' + apellido2, apellido1, apellido2, nacionalidad)
        .withDocument(numeroDocumento, soporteDocumento, tipoDocumento, pais)
        .withDates(fechaNacimiento, fechaExpedicion, fechaCaducidad)
        .withAddress(direccion, cityAddress, stateAddress, codigoPostal, provinceId, municipalityId)
        .withContract(tipoContrato)
        .build();

      this.checkInService.setCheckInAdult(checkInData).subscribe({
        next: (response) => {
          this.metricsService.checkInAdultRegistered();
          this.loading.set(false);
          if (ageVal < 18) {
            this.hoomvipStorage.unmarkCheckingWarning();
            this.hoomvipStorage.setCheckInMinorSignatureInfo(response.travelRegisterId, email);
            this.router.navigate(['/check-in-signature-minor']);
          } else {
            this.hoomvipStorage.setCheckInSignatureInfo(response.travelRegisterId, response.contractUrl, email);
            this.router.navigate(['/check-in-signature']);
          }
        },
        error: (error) => {
          this.loading.set(false);
          this.modalErrorService.open('Error en el check-in', this.modalErrorService.extractMessages(error));
        }
      });
    }
  }

  openSoporteDocumentoInfo(): void {
    this.modalInfoService.open(
      'Nº de Soporte del DNI o NIE',
      [],
      [
        'images/num_sop_1.png',
        'images/num_sop_2.png',
        'images/num_sop_3.png'
      ]
    );
  }

  private calculateAge(birthDateString: string): number {
    if (!birthDateString) return 0;
    const today = new Date();
    const birthDate = new Date(birthDateString);
    if (isNaN(birthDate.getTime())) return 0;

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  private updateFormFieldsByAge(): void {
    const ageVal = this.age();
    const documentFields = ['tipoDocumento', 'numeroDocumento', 'soporteDocumento', 'fechaExpedicion', 'fechaCaducidad'];
    const contractFields = ['tipoContrato'];

    if (ageVal < 14) {
      documentFields.concat(contractFields).forEach(field => {
        this.form.get(field)?.clearValidators();
        this.form.get(field)?.disable({emitEvent: false});
      });
    } else {
      documentFields.concat(contractFields).forEach(field => this.form.get(field)?.enable({emitEvent: false}));
      this.form.get('tipoDocumento')?.setValidators([Validators.required]);
      this.form.get('numeroDocumento')?.setValidators([Validators.required, CheckInValidators.documentNumberByType('tipoDocumento')]);
      this.form.get('soporteDocumento')?.setValidators([CheckInValidators.requiredWhenDocumentTypeIs('tipoDocumento', ['D', 'N'])]);
      this.form.get('fechaExpedicion')?.setValidators([Validators.required]);
      if (ageVal >= 18 && this.contractNecessary()) {
        this.form.get('tipoContrato')?.setValidators([Validators.required]);
      } else {
        this.form.get('tipoContrato')?.clearValidators();
      }
      this.form.get('tipoContrato')?.updateValueAndValidity({emitEvent: false});
    }
    this.form.updateValueAndValidity({emitEvent: false});
  }
}
